/**
 * Lead List Page
 * View and manage all leads in the sales pipeline
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdFilterList,
  MdPhone,
  MdEmail,
  MdMoreVert,
  MdVisibility,
  MdEdit,
  MdAdd,
  MdRefresh,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Permission } from '../../../types/permissions';
import {
  getLeads,
  getLeadStats,
} from '../../../services/leadManagement';
import {
  Lead,
  LeadFilters,
  LeadStats,
  getLeadStatusDisplay,
  getLeadStatusColor,
} from '../../../types/lead';

export function LeadList() {
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<LeadFilters>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Load leads and stats
  const loadData = async () => {
    try {
      setLoading(true);
      const [leadsData, statsData] = await Promise.all([
        getLeads(filters),
        getLeadStats(),
      ]);
      setLeads(leadsData.leads);
      setStats(statsData);
    } catch (error) {
      toast({
        title: 'Error loading leads',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters]);

  const handleFilterChange = (key: keyof LeadFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({
      ...prev,
      search: searchTerm || undefined,
    }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading && !leads.length) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Statistics Cards */}
      {stats && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="20px" mb="20px">
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Pipeline Value</StatLabel>
                <StatNumber>{formatCurrency(stats.totalPipelineValue)}</StatNumber>
                <StatHelpText>
                  Weighted: {formatCurrency(stats.weightedPipelineValue)}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Leads</StatLabel>
                <StatNumber>{stats.total}</StatNumber>
                <StatHelpText>
                  Active: {stats.total - stats.byStatus.closed_won - stats.byStatus.closed_lost}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Avg Door Count</StatLabel>
                <StatNumber>{Math.round(stats.avgDoorCount)}</StatNumber>
                <StatHelpText>Avg Score: {Math.round(stats.avgScore)}/100</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Close Rate</StatLabel>
                <StatNumber>{stats.conversionRates.overallCloseRate.toFixed(1)}%</StatNumber>
                <StatHelpText>
                  Won: {stats.byStatus.closed_won || 0} deals
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Main Card */}
      <Card>
        <CardHeader>
          <Flex justify="space-between" align="center" mb="4">
            <Heading size="md">Sales Pipeline</Heading>
            <HStack>
              <IconButton
                aria-label="Refresh"
                icon={<MdRefresh />}
                onClick={loadData}
                isLoading={loading}
              />
              {hasPermission(Permission.USER_CREATE) && (
                <Button
                  leftIcon={<MdAdd />}
                  colorScheme="brand"
                  onClick={() => navigate('/admin/sales/leads/new')}
                >
                  New Lead
                </Button>
              )}
            </HStack>
          </Flex>

          {/* Filters */}
          <Stack spacing="4">
            <HStack spacing="4">
              <InputGroup maxW="400px">
                <InputLeftElement>
                  <Icon as={MdSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name, email, or company..."
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </InputGroup>

              <Select
                maxW="200px"
                placeholder="All Statuses"
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
              >
                <option value="new">New Lead</option>
                <option value="qualifying">Qualifying</option>
                <option value="demo_scheduled">Demo Scheduled</option>
                <option value="demo_completed">Demo Completed</option>
                <option value="proposal_sent">Proposal Sent</option>
                <option value="negotiating">Negotiating</option>
                <option value="closed_won">Closed Won</option>
                <option value="closed_lost">Closed Lost</option>
              </Select>

              <Select
                maxW="200px"
                placeholder="All Sources"
                onChange={(e) => handleFilterChange('source', e.target.value || undefined)}
              >
                <option value="website">Website</option>
                <option value="linkedin">LinkedIn</option>
                <option value="referral">Referral</option>
                <option value="cold_outreach">Cold Outreach</option>
                <option value="paid_ad">Paid Ad</option>
              </Select>

              <Select
                maxW="200px"
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                value={filters.sortBy}
              >
                <option value="createdAt">Date Created</option>
                <option value="updatedAt">Last Updated</option>
                <option value="score">Lead Score</option>
                <option value="estimatedValue">Value</option>
              </Select>
            </HStack>
          </Stack>
        </CardHeader>

        <CardBody>
          {leads.length === 0 ? (
            <Flex direction="column" align="center" justify="center" py="12">
              <Icon as={MdFilterList} boxSize="12" color="gray.300" mb="4" />
              <Text color="gray.500">No leads found</Text>
            </Flex>
          ) : (
            <>
              {/* Pagination and Page Size Info */}
              <Flex justify="space-between" align="center" mb="4">
                <HStack spacing="4">
                  <Text fontSize="sm" color="gray.600">
                    Showing {Math.min((currentPage - 1) * pageSize + 1, leads.length)} to {Math.min(currentPage * pageSize, leads.length)} of {leads.length}
                  </Text>
                  <Select
                    maxW="150px"
                    size="sm"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="10">10 per page</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                  </Select>
                </HStack>
              </Flex>

              <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Lead</Th>
                    <Th>Contact</Th>
                    <Th>Doors</Th>
                    <Th>Status</Th>
                    <Th>Source</Th>
                    <Th>Score</Th>
                    <Th>Value</Th>
                    <Th>Assigned To</Th>
                    <Th>Created</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {leads
                    .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                    .map((lead) => (
                    <Tr
                      key={lead.id}
                      _hover={{ bg: 'gray.50' }}
                      cursor="pointer"
                      onClick={() => navigate(`/admin/sales/leads/${lead.id}`)}
                    >
                      <Td>
                        <VStack align="start" spacing="1">
                          <Text fontWeight="bold">{lead.contactInfo.name}</Text>
                          {lead.contactInfo.company && (
                            <Text fontSize="sm" color="gray.500">
                              {lead.contactInfo.company}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <VStack align="start" spacing="1">
                          <HStack>
                            <Icon as={MdEmail} boxSize="3" color="gray.400" />
                            <Text fontSize="sm">{lead.contactInfo.email}</Text>
                          </HStack>
                          {lead.contactInfo.phone && (
                            <HStack>
                              <Icon as={MdPhone} boxSize="3" color="gray.400" />
                              <Text fontSize="sm">{lead.contactInfo.phone}</Text>
                            </HStack>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <Text fontWeight="semibold">{lead.businessInfo.doorCount}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {lead.businessInfo.pricingTier}
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={getLeadStatusColor(lead.status)}>
                          {getLeadStatusDisplay(lead.status)}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="sm" textTransform="capitalize">
                          {lead.source.replace('_', ' ')}
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={
                            lead.score >= 70 ? 'green' : lead.score >= 40 ? 'yellow' : 'red'
                          }
                        >
                          {lead.score}
                        </Badge>
                      </Td>
                      <Td>
                        <VStack align="start" spacing="0">
                          <Text fontWeight="semibold">
                            {formatCurrency(lead.estimatedValue)}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {lead.probability}% prob
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {lead.assignedToName || (
                            <Text color="gray.400">Unassigned</Text>
                          )}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">{formatDate(lead.createdAt)}</Text>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<MdMoreVert />}
                            variant="ghost"
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <MenuList>
                            <MenuItem
                              icon={<MdVisibility />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/sales/leads/${lead.id}`);
                              }}
                            >
                              View Details
                            </MenuItem>
                            <MenuItem
                              icon={<MdEdit />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/sales/leads/${lead.id}/edit`);
                              }}
                            >
                              Edit
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination Controls */}
            <Flex justify="center" align="center" mt="6" gap="2">
              <Button
                size="sm"
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                First
              </Button>
              <Button
                size="sm"
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              <HStack spacing="1">
                {Array.from({ length: Math.ceil(leads.length / pageSize) }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === Math.ceil(leads.length / pageSize) ||
                      Math.abs(page - currentPage) <= 1
                  )
                  .map((page, idx, arr) => (
                    <div key={page}>
                      {idx > 0 && arr[idx - 1] !== page - 1 && <Text>...</Text>}
                      <Button
                        size="sm"
                        colorScheme={page === currentPage ? 'brand' : 'gray'}
                        variant={page === currentPage ? 'solid' : 'outline'}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    </div>
                  ))}
              </HStack>
              <Button
                size="sm"
                isDisabled={currentPage === Math.ceil(leads.length / pageSize)}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
              <Button
                size="sm"
                isDisabled={currentPage === Math.ceil(leads.length / pageSize)}
                onClick={() => setCurrentPage(Math.ceil(leads.length / pageSize))}
              >
                Last
              </Button>
            </Flex>
            </>
          )}
        </CardBody>
      </Card>
    </Box>
  );
}
